import { UIIcon } from '../config/icons';
import { cProducerRoleInviteDelete, cProducerRoleInviteResend, cProducerRoleInviteSend } from '../lib/mdl/c-producer';
import { CProducerRole, cProducerRoleDelete, CProducerRoleInvite, CProducerRoleInviteUserView, cProducerRolesGet, cProducerRoleUpdate, CUserProducerRoleUserView } from '../lib/mdl/c-producer-admin';
import { the_user } from '../lib/me/login';
import { the_producer } from '../lib/me/producer';
import { UIAdapter, UIDataAdapter } from '../lib/ui/component/adapter';
import { UIButton } from '../lib/ui/component/button';
import { UIDialog } from '../lib/ui/component/dialog';
import { UITextInput } from '../lib/ui/component/input';
import { UIList } from '../lib/ui/component/list';
import { uiToast } from '../lib/ui/component/toast';
import { UIExclusiveToggles, UIFlagsToggles, UITickCheckbox, UITickRadio } from '../lib/ui/component/toggle';
import { showWarning } from '../lib/ui/component/warning';
import { $el } from '../lib/ui/elements';
import { UIScreen } from '../lib/ui/screen';
import { UIView, UIViewSpec } from '../lib/ui/view';
import { $analytics } from '../lib/y/analytics';
import { $on } from '../lib/y/events';
import { $fn } from '../lib/y/fn';
import { $form } from '../lib/y/form';
import { $i18n, yInterpolate } from '../lib/y/i18n';
import { YFlags } from '../lib/y/types';

class InviteDialog extends UIDialog {
    constructor(role?: CUserProducerRoleUserView) {
        super({ classes: 'm-500 m-mobile-fs' });

        const email = new UITextInput({ kind: 'k-subtle', type: 'email', placeholder: $i18n.tl.ph_email, required: true });

        const admin_values = [
            { key: 'admin', title: $i18n.pr.team_admin, subtitle: $i18n.pr.team_admin_description },
            { key: 'others', title: $i18n.pr.team_limited_access },
        ];

        const admin = new UIExclusiveToggles(admin_values, v => new UITickRadio(v), { classes: 'l-form' })

        const role_values = [
            { key: 'events' as CProducerRole, title: $i18n.pr.team_events, subtitle: $i18n.pr.team_events_description },
            { key: 'bookings' as CProducerRole, title: $i18n.pr.team_bookings, subtitle: $i18n.pr.team_bookings_description },
            { key: 'economy' as CProducerRole, title: $i18n.pr.team_economy, subtitle: $i18n.pr.team_economy_description }
        ];

        $on(admin, 'value', v => {
            roles.disabled = v === 'admin';
        });

        const roles = new UIFlagsToggles(role_values, v => new UITickCheckbox(v), { required: true, classes: 'l-form tl-permission-box' });

        const form = $form({});

        if(!role) {
            form.add({ email: email });
        }

        const producer_id = the_producer!.id;

        if(role) {
            admin.value = 'others';

            if(role.roles['admin'])
                admin.value = 'admin';
            else
                roles.value = role.roles;
        } else {
            admin.value = 'admin';
        }

        this.content.ui_add($el.DIV('w-block l-form m-compact', [
            $el.H3('title', role ? role.user_first_name + ' ' + role.user_last_name : $i18n.pr.team_invite),
            $el.DIV('s-small'),
            !role && [email, $el.DIV('s-small')],
            $el.DIV('l-form m-real-left', [
                admin,
                roles,
            ]),
            $el.DIV('s-medium'),
            $el.DIV('l-button-row', [
                new UIButton({ title: $i18n.tl.b_cancel, classes: 'm-hollow ui-tint-reverse', on_click: () => { this.dismiss(null) } }),
                role ? new UIButton({
                    title: $i18n.pr.team_update_permissions, classes: 'ui-tint-reverse', on_click: () => {
                        if(!form.validate()) return;
                        const roles_val: YFlags<CProducerRole> = admin.value === 'admin' ? { admin: true } : roles.value;
                        if(!Object.keys(roles_val).length) {
                            roles.markInvalid(true, true);
                            return;
                        }

                        return cProducerRoleUpdate(role, roles_val).then(() => {
                            $analytics.track('Edited Permission', { mate_email: role.user_email, roles: Object.keys(roles_val).join(', ') });
                            this.dismiss(true);
                        }, uiToast);
                    }
                }) : new UIButton({
                    title: $i18n.pr.team_send_invite, classes: 'ui-tint-reverse', on_click: () => {
                        if(!form.validate()) return;
                        const email_val = email.value;
                        const roles_val: YFlags<CProducerRole> = admin.value === 'admin' ? { admin: true } : roles.value;
                        if(!Object.keys(roles_val).length) {
                            roles.markInvalid(true, true);
                            return;
                        }

                        return cProducerRoleInviteSend(email_val, producer_id, Object.keys(roles_val)).then(() => {
                            $analytics.track('Invited Team Mate', { mate_email: email_val, roles: Object.keys(roles_val).join(', ') });
                            this.dismiss(true);
                        }, uiToast);
                    }
                })
            ])
        ]));
    }
}

function rolesToDescription(roles: CProducerRole[] | undefined, role_names: { [key: string]: string }) {
    return roles?.map(r => role_names[r]).filter(r => r).join(', ') ?? '';
}

class RoleCard extends UIView {
    constructor(role: CUserProducerRoleUserView, role_names: { [key: string]: string }, callback: () => void, removeCallback: () => void) {
        const remove_button = new UIButton({
            kind: 'k-text', icon: UIIcon.TRASH_CAN, classes: 'ui-tint-fg', on_click: () => {
                return showWarning({ text: $i18n.pr.team_remove_confirm, cancel_text: $i18n.tl.b_cancel, ok_text: $i18n.tl.b_confirm, button_classes: 'ui-tint-reverse' }).then(() => {
                    cProducerRoleDelete(the_producer!.id, role.user_id).then(() => {
                        $analytics.track('Removed Team Mate', { mate_email: role.user_email });
                        removeCallback();
                    });
                });
            }
        });

        if(role.user_id == the_user!.id)
            remove_button.disabled = true;

        super('div', 'tl-producer-card m-user', [
            $el.DIV('r-info', [
                $el.B(role.user_first_name + ' ' + role.user_last_name),
                $el.B(role.user_email),
                $el.B(rolesToDescription(Object.keys(role.roles), role_names))
            ]),
            $el.S(),
            new UIButton({
                kind: 'k-square', icon: UIIcon.PEN_TO_SQUARE, classes: 'm-round m-bigger ui-tint-subtle', on_click: () => {
                    return new InviteDialog(role).present().then(callback);
                }
            }),
            $el.DIV('s-tiny'),
            remove_button
        ]);
    }
}

class InviteCard extends UIView {
    constructor(invite: CProducerRoleInvite, role_names: { [key: string]: string }, _callback: () => void, removeCallback: () => void) {
        const remove_button = new UIButton({
            kind: 'k-text', icon: UIIcon.TRASH_CAN, classes: 'ui-tint-fg', on_click: () => {
                return showWarning({ text: $i18n.pr.team_remove_confirm, cancel_text: $i18n.tl.b_cancel, ok_text: $i18n.tl.b_confirm, button_classes: 'ui-tint-reverse' }).then(() => {
                    cProducerRoleInviteDelete(invite.id).then(removeCallback);
                });
            }
        });

        const resend_button = new UIButton({
            kind: 'k-square', icon: UIIcon.SHARE, classes: 'm-round m-bigger ui-tint-subtle', on_click: () => {
                cProducerRoleInviteResend(invite.id).then(() => {
                    resend_button.ui_hide();
                })
            }
        });


        super('div', 'tl-producer-card m-user', [
            $el.DIV('r-info', [
                $el.B(invite.dst_email),
                $el.B(rolesToDescription(Object.keys(invite.roles), role_names))
            ]),
            $el.S(),
            resend_button,
            $el.DIV('s-tiny'),
            remove_button
        ]);
    }
}

export class ProducerTeamScreen extends UIScreen {
    private _all_roles!: CUserProducerRoleUserView[];
    private _all_invites!: CProducerRoleInviteUserView[];
    private _list!: UIList;

    constructor() {
        super();
    }

    protected override async fillContent(content: UIViewSpec) {
        const { roles, invites } = await cProducerRolesGet(the_producer!.id);
        this._all_roles = roles;
        this._all_invites = invites;

        const doSearch = () => this._doSearch();

        const add_button = new UIButton({
            title: $i18n.pr.team_add_member, left_icon: UIIcon.PLUS, classes: 'm-compact m-round ui-tint-subtle b', on_click: () => {
                return new InviteDialog().present().then(async () => {
                    const { roles, invites } = await cProducerRolesGet(the_producer!.id);
                    this._all_roles = roles;
                    this._all_invites = invites;
                    doSearch();
                });
            }
        });

        content.ui_add($el.DIV('ui-section', $el.DIV('tl-new-list-block', [
            $el.P('screen-title', yInterpolate($i18n.pr.team_title, the_producer!.name)),
            $el.P('screen-mobile-title', yInterpolate($i18n.pr.team_mobile_title, the_producer!.name)),
            $el.DIV('s-medium'),
            add_button,
            this._list = new UIList(undefined, { classes: 'tl-producer-list b', progressive: true })
        ])));

        doSearch();
    }

    private _doSearch() {
        let roles = this._all_roles;
        let invites = this._all_invites;
        if(!roles || !invites) return;

        const role_names = {
            admin: $i18n.pr.team_admin,
            events: $i18n.pr.team_events,
            bookings: $i18n.pr.team_bookings,
            economy: $i18n.pr.team_economy
        }

        const role_section = new UIDataAdapter((r: CUserProducerRoleUserView) => new RoleCard(r, role_names, () => { this._doSearch() }, () => {
            this._all_roles = this._all_roles.filter(o => o != r);
            this._doSearch();
        }));
        role_section.data = roles.sort((a, b) => $fn.ICOMPARE(a.user_first_name + ' ' + a.user_last_name, b.user_first_name + ' ' + b.user_last_name));

        const sections: UIAdapter<UIViewSpec>[] = [role_section];

        if(invites.length) {
            const invite_section = new UIDataAdapter((i: CProducerRoleInvite) => new InviteCard(i, role_names, () => { this._doSearch() }, () => {
                this._all_invites = this._all_invites.filter(ou => ou.id != i.id);
                this._doSearch();
            }));
            invite_section.header = (() => $el.P('heading', $i18n.pr.team_invited_users))
            invite_section.data = invites.sort((a, b) => $fn.ICOMPARE(a.dst_email, b.dst_email));
            sections.push(invite_section);
        }

        this._list.sections = sections;
    }
}
